<template>
  <label
    :class="allClasses"
    tabindex="0"
    v-on="listeners"
    @keydown.enter.space.stop.prevent="delegateInput"
  >
    <input
      ref="inputElement"
      :value="value"
      :checked="checked"
      v-bind="{ disabled, required, name, ...$attrs }"
      class="radio__input"
      type="radio"
      tabindex="-1"
      data-test-id="radio-input"
      @click="onInput"
    >
    <slot />
  </label>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

/**
 * The radio button is a custom wrapper around the native browser
 * input, and include it's label. There is no use case for a single
 * radio button component to be used. The radio button group should
 * be always used.
 */
export default {
  name: 'ERadio',
  mixins: [classes],
  model: {
    prop: 'modelValue',
    event: 'input',
  },
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: 'm',
      cssClass: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  computed: {
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (this.checked) {
        allClasses.push('radio--checked');
      }
      return allClasses;
    },
    checked() {
      return this.modelValue === this.value;
    },
  },
  methods: {
    onInput({ target: { value } }) {
      this.$emit('input', value);
    },
    delegateInput() {
      this.$refs.inputElement.click();
    },
  },
};
</script>

<style lang="scss" scoped>
$radio-size-m: $space-l;
$radio-size-s: 16px;
$radio-size-s-dot-size: 6px;
$radio-size-dot-size: 7px;
$tappable-area-padding: $space-1s;

.radio {
  display: flex;
  position: relative;
  font-family: $family;
  line-height: $line-height-m;
  color: $color-text-primary;
  cursor: pointer;
  padding-top: $space-1s;
  padding-bottom: $space-1s;

  &::before {
    content: "";
    display: block;
    border-radius: $border-radius-full;
    border: solid $border-width $color-border;
    box-sizing: border-box;
    pointer-events: none;
    transition: border $timing-2s $easing;
  }

  &:hover {
    &::before {
      border-color: $color-border-hover-primary;
    }
  }

  &:focus {
    @extend %keyboard-nav-focus;

    &::before {
      border-color: $color-border-focus-primary;
    }
  }
}

.radio__input {
  border: none;
  cursor: pointer;
  height: calc(100% + #{2 * $tappable-area-padding});
  left: -1 * $tappable-area-padding;
  margin: 0;
  opacity: 0;
  position: absolute;
  top: -1 * $tappable-area-padding;
}

.radio--size-m {
  font-size: $font-l;

  &::before {
    width: $radio-size-m;
    height: $radio-size-m;
    margin-right: $space-m;
  }

  .radio__input {
    width: calc(#{$radio-size-m} + #{2 * $tappable-area-padding});
  }
}

.radio--size-s {
  font-size: $font-s;

  &::before {
    width: $radio-size-s;
    height: $radio-size-s;
    margin-right: 7px;
  }

  &.radio--active {
    &::before {
      border-width: $radio-size-s-dot-size;
      border-color: $color-brand-primary;
    }
  }

  .radio__input {
    width: calc(#{$radio-size-s} + #{2 * $tappable-area-padding});
  }
}

.radio--checked {
  &::before {
    border-width: $radio-size-dot-size;
    border-color: $color-brand-primary;
  }
}

.radio--disabled {
  pointer-events: none;
  color: $gray-30;

  &::before {
    border-color: $color-disabled;
  }

  &.radio--active {
    &::before {
      border-color: rgba($color-brand-primary, $opacity-m);
    }
  }
}
</style>

<docs>
A radio button

### Examples

#### Default

##### Size M

```js
let model = '';
<div>
  <ERadio v-model="model" value="1">This is an option</ERadio>
  <br/>
  The value is {{model}}
</div>
```

```js
let model = '';
<ERadio v-model="model" value="option">
  Lorem ipsum, or lipsum as it is sometimes known, is dummy text used in laying out
  print, graphic or web designs. The passage is attributed to an unknown typesetter
  in the 15th century who is thought to have scrambled parts of Cicero's De Finibus
  Bonorum et Malorum for use in a type specimen book.
</ERadio>
```

##### size S

```js
let model = '';
<ERadio v-model="model" value="option" size="s">This is an option</ERadio>
```

#### Disabled

```js
let model = '';
<ERadio v-model="model" value="option" disabled>This is an option</ERadio>
```

#### Active

```js
let model = 'value';
<ERadio v-model="model" value="value">This is option active</ERadio>
```

```js
let model = 'value';
<ERadio v-model="model" value="value" disabled>This is option active and disabled</ERadio>
```

</docs>
